import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Empty, List, Typography } from 'antd';

import {
    type Notification,
    useGetNotificationsQuery,
    useMarkAllAsReadMutation,
    useMarkAsReadMutation,
} from '@/entities/notification';

import { NotificationItem } from '../notification-item/notification-item';
import { NotificationModal } from '../notification-modal/notification-modal';

import cls from './notification-list.module.css';

type NotificationListProps = {
    onClose?: () => void;
};

export const NotificationList = memo<NotificationListProps>(({ onClose }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] =
        useState<Notification | null>(null);

    // const [currentOffset, setCurrentOffset] = useState(0);

    const { data, isLoading } = useGetNotificationsQuery({
        offset: 0,
    });

    const [markAllAsRead] = useMarkAllAsReadMutation();
    const [markAsRead] = useMarkAsReadMutation();

    // Check if there are any unread notifications
    const hasUnreadNotifications = data?.results.some(
        (notification) => !notification.read,
    );

    const handleMarkAllAsRead = useCallback(() => {
        if (hasUnreadNotifications) {
            markAllAsRead();
        }
    }, [hasUnreadNotifications, markAllAsRead]);

    // Handle opening the notification modal
    const handleOpenModal = useCallback(
        (notification: Notification) => {
            setSelectedNotification(notification);
            setIsModalOpen(true);
            markAsRead(notification.id);
            onClose?.();
        },
        [onClose, markAsRead],
    );

    // Handle closing the notification modal
    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setSelectedNotification(null);
    }, []);

    if (!data?.results.length) {
        return <Empty description={t('notification.noNotifications')} />;
    }

    return (
        <div>
            <div className={cls.header}>
                <Typography.Title level={4}>
                    {t('notification.notificationTitle')}
                </Typography.Title>
                <Button
                    disabled={!hasUnreadNotifications}
                    onClick={handleMarkAllAsRead}
                    size="small"
                    type="link"
                >
                    {t('notification.markAllAsRead')}
                </Button>
            </div>

            <List
                className={cls.notificationList}
                dataSource={data.results}
                loading={isLoading}
                renderItem={(notification: Notification) => (
                    <NotificationItem
                        key={notification.id}
                        loading={isLoading}
                        notification={notification}
                        onOpenModal={handleOpenModal}
                    />
                )}
                rowKey={(notification: Notification) => notification.id}
            />

            <NotificationModal
                isOpen={isModalOpen}
                notification={selectedNotification}
                onClose={handleCloseModal}
            />
        </div>
    );
});

NotificationList.displayName = 'NotificationList';
